/**
 * Hub / My Assessments cadence lines.
 * An input counts in the cycle it was given and the next cycle (yellow, still
 * carried). From the cycle after that it is excluded (red) until they reassess.
 */

export type CadenceSection = "current" | "stale";
export type CadenceAssignment = "assigned" | "unassigned";
export type CadenceTone = "green" | "yellow" | "red" | "neutral";

export type CadenceTag = {
  tone: CadenceTone;
  mark: "✓" | "✕" | "—";
  label: "Carried In" | "Reassess" | "Date" | "Not assessed";
  /** Last submitted date for this account. Empty when never submitted. */
  value: string;
  /** Elapsed since that submission, for the hover. */
  elapsed?: string;
};

/** Hover copy: why this tag is shown, and when they last submitted. */
export const cadenceTagTitle = (tag: CadenceTag): string => {
  if (tag.label === "Not assessed") {
    return "No input on this account, so nothing is counted until they submit.";
  }
  const ago =
    tag.elapsed && !tag.elapsed.startsWith("0 ") ? ` (${tag.elapsed} ago)` : "";
  const when = `${tag.value}${ago}`;
  if (tag.tone === "green" && tag.label === "Carried In") {
    return `Prior input is still in the cycle it was given, so it is carried into this assessment. Last submitted for this account on ${when}.`;
  }
  if (tag.tone === "green" && tag.label === "Reassess") {
    return `This executive submitted a fresh input in the current cycle. Last submitted for this account on ${when}.`;
  }
  if (tag.tone === "yellow") {
    return `The input is in the next cycle and is still counted on publish. Last submitted for this account on ${when}.`;
  }
  return `The input is past two cycles, so it is not counted until they reassess. Last submitted for this account on ${when}.`;
};

export type CadencePlacement = {
  section: CadenceSection;
  assignment: CadenceAssignment;
  tag: CadenceTag | null;
};

type CadenceNotes = {
  origin?: string | null;
  carry_forward?: boolean | null;
  carried_in_at_publish?: boolean | null;
  excluded?: {
    reason?: string | null;
    submitted_on?: string | null;
    days_since?: number | null;
  } | null;
  stale_at_publish?: boolean | null;
} | null;

export type CadenceExecutiveInput = {
  notes?: CadenceNotes;
  submitted_at?: string | null;
  is_responded?: boolean;
  carried?: boolean;
};

const UNASSIGNED_ORIGINS = new Set([
  "included_member",
  "unassigned_self",
  "cadence_current",
]);

const QUARTERLY = [1, 4, 7, 10];
const BIMONTHLY = [1, 3, 5, 7, 9, 11];

const periodMonths = (cadenceDays: number): number[] =>
  cadenceDays === 60 ? BIMONTHLY : QUARTERLY;

const utcDate = (value: string | Date): Date | null => {
  const parsed = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  return new Date(
    Date.UTC(parsed.getUTCFullYear(), parsed.getUTCMonth(), parsed.getUTCDate())
  );
};

const periodStartsForYear = (year: number, cadenceDays: number): Date[] =>
  periodMonths(cadenceDays).map(
    month => new Date(Date.UTC(year, month - 1, 1))
  );

const periodStartFor = (day: Date, cadenceDays: number): Date => {
  const starts = [
    ...periodStartsForYear(day.getUTCFullYear() - 1, cadenceDays),
    ...periodStartsForYear(day.getUTCFullYear(), cadenceDays),
  ];
  return starts
    .filter(start => start.getTime() <= day.getTime())
    .sort((a, b) => b.getTime() - a.getTime())[0];
};

const laterPeriodStarts = (own: Date, cadenceDays: number): Date[] => {
  const years = [own.getUTCFullYear(), own.getUTCFullYear() + 1];
  return years
    .flatMap(year => periodStartsForYear(year, cadenceDays))
    .filter(start => start.getTime() > own.getTime())
    .sort((a, b) => a.getTime() - b.getTime());
};

/** First day the input stops counting (start of the cycle after the grace cycle). */
export const inputStaleOn = (submittedDay: Date, cadenceDays: number): Date => {
  const own = periodStartFor(submittedDay, cadenceDays);
  return laterPeriodStarts(own, cadenceDays)[1];
};

const nextPeriodStart = (submittedDay: Date, cadenceDays: number): Date => {
  const own = periodStartFor(submittedDay, cadenceDays);
  return laterPeriodStarts(own, cadenceDays)[0];
};

const formatDate = (day: Date): string =>
  day.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  });

const daysBetween = (from: Date, to: Date): number | null => {
  const days = Math.round((to.getTime() - from.getTime()) / 86400000);
  return days > 0 ? days : null;
};

const isCarried = (exec: CadenceExecutiveInput): boolean => {
  const notes = exec.notes;
  if (exec.carried) return true;
  if (!notes) return false;
  return (
    notes.origin === "carried_in" ||
    notes.carried_in_at_publish === true ||
    notes.carry_forward === true
  );
};

const isUnassigned = (exec: CadenceExecutiveInput): boolean =>
  UNASSIGNED_ORIGINS.has((exec.notes?.origin || "").toLowerCase());

const submittedDayOf = (exec: CadenceExecutiveInput): Date | null => {
  const excludedOn = exec.notes?.excluded?.submitted_on;
  const excludedDay = excludedOn ? utcDate(excludedOn) : null;
  const responseDay = exec.submitted_at ? utcDate(exec.submitted_at) : null;
  const staleRecord =
    exec.notes?.excluded?.reason === "stale" ||
    exec.notes?.stale_at_publish === true;
  // Hub publish stamps submitted_at on a not-assessed assignment. That close
  // date is not an input; only a prior submitted_on on the exclusion counts.
  if (exec.notes?.excluded?.reason === "not_assessed") return excludedDay;
  // Publish rows stamp submitted_at as the close day. The exclusion note keeps
  // the real last input for this account.
  if (staleRecord && excludedDay) return excludedDay;
  if (
    responseDay &&
    excludedDay &&
    excludedDay.getTime() < responseDay.getTime()
  ) {
    return excludedDay;
  }
  return responseDay || excludedDay;
};

export const classifyCadenceExecutive = (
  exec: CadenceExecutiveInput,
  cadenceDays = 90,
  onDay: Date = new Date()
): CadencePlacement => {
  const assignment: CadenceAssignment = isUnassigned(exec)
    ? "unassigned"
    : "assigned";
  const today = utcDate(onDay) || onDay;
  const submitted = submittedDayOf(exec);
  const excludedStale =
    exec.notes?.excluded?.reason === "stale" ||
    exec.notes?.stale_at_publish === true;

  if (!submitted) {
    return {
      section: "current",
      assignment,
      tag: {
        tone: "neutral",
        mark: "—",
        label: "Not assessed",
        value: "",
      },
    };
  }

  const staleOn = inputStaleOn(submitted, cadenceDays);
  const graceStart = nextPeriodStart(submitted, cadenceDays);
  const notedDays = exec.notes?.excluded?.days_since;
  const elapsedDays =
    typeof notedDays === "number" && notedDays > 0
      ? notedDays
      : daysBetween(submitted, today);
  const elapsed = elapsedDays != null ? `${elapsedDays} days` : undefined;
  const submittedLabel = formatDate(submitted);

  if (excludedStale || today.getTime() >= staleOn.getTime()) {
    // Past grace the value is not counted, so the person leaves Assigned.
    return {
      section: "stale",
      assignment: "unassigned",
      tag: {
        tone: "red",
        mark: "✕",
        label: "Date",
        value: submittedLabel,
        elapsed,
      },
    };
  }

  if (today.getTime() >= graceStart.getTime()) {
    return {
      section: "stale",
      assignment,
      tag: {
        tone: "yellow",
        mark: "✕",
        label: "Carried In",
        value: submittedLabel,
        elapsed,
      },
    };
  }

  return {
    section: "current",
    assignment,
    tag: isCarried(exec)
      ? {
          tone: "green",
          mark: "✓",
          label: "Carried In",
          value: submittedLabel,
          elapsed,
        }
      : {
          tone: "green",
          mark: "✓",
          label: "Reassess",
          value: submittedLabel,
          elapsed,
        },
  };
};

export const CADENCE_TAG_CLASS: Record<CadenceTone, string> = {
  green:
    "inline-flex w-max max-w-full shrink-0 cursor-pointer items-center gap-1 whitespace-nowrap rounded-full border border-green-200 bg-green-50 px-2.5 py-0.5 text-xs font-medium leading-none text-green-700",
  yellow:
    "inline-flex w-max max-w-full shrink-0 cursor-pointer items-center gap-1 whitespace-nowrap rounded-full border border-yellow-300 bg-yellow-50 px-2.5 py-0.5 text-xs font-medium leading-none text-yellow-800",
  red: "inline-flex w-max max-w-full shrink-0 cursor-pointer items-center gap-1 whitespace-nowrap rounded-full border border-red-200 bg-red-50 px-2.5 py-0.5 text-xs font-medium leading-none text-red-700",
  neutral:
    "inline-flex w-max max-w-full shrink-0 cursor-pointer items-center gap-1 whitespace-nowrap rounded-full border border-gray-200 bg-gray-50 px-2.5 py-0.5 text-xs font-medium leading-none text-gray-600",
};
